import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TakeUntilDestroy } from '@app/shared/decorators/take-until-destroy.decorator';
import { LimitedTimeEvent } from '@app/shared/models/limitedTimeEvent.model';
import { Modal, ModalType, NonPersistedModalName } from '@app/shared/models/modal.model';
import { ShopOffersItem } from '@app/shared/models/shop-offers.model';
import { LoggerService } from '@app/shared/services/logger.service';
import { EventManager } from '@app/shared/utils/event-manager';
import { TranslateService } from '@ngx-translate/core';
import { Observable, from } from 'rxjs';
import { max } from 'rxjs/operators';
import { LimitedTimeEventsLayoutComponent } from '@app/shared/components/liveops/limited-time-events-layout/limited-time-events-layout.component';
import { BoxComponent } from '@app/shared/components/liveops/box/box.component';
import { AnimatedTimerComponent } from '@app/shared/components/animated-timer/animated-timer.component';
import { ButtonComponent } from '@app/shared/components/button/button.component';
import { SharedModule } from '@app/shared/shared.module';
import { EventsRepository } from '@app/shared/state/events/events.repository';
import { ShopRatesRepository } from '@app/shared/state/shop-rates/shop-rates.repository';

@TakeUntilDestroy
@Component({
  selector: 'slotcom-randomprize-modal',
  templateUrl: './randomprize.component.html',
  styleUrls: ['./randomprize.component.scss'],
  standalone: true,
  imports: [LimitedTimeEventsLayoutComponent, BoxComponent, AnimatedTimerComponent, ButtonComponent, SharedModule],
})
export class RandomPrizeModalComponent implements OnInit {
  componentDestroy: () => Observable<unknown>;

  private readonly eventManager = inject(EventManager);
  private readonly eventsRepo = inject(EventsRepository);
  private readonly shopRatesRepo = inject(ShopRatesRepository);
  private readonly translateService = inject(TranslateService);
  public readonly logger = inject(LoggerService);
  protected readonly randomprizeModalData = inject<Modal>(MAT_DIALOG_DATA);

  NonPersistedModalName = NonPersistedModalName;

  randomprize = signal<LimitedTimeEvent | null>(null);
  lang = signal<string>('');

  randomprizeMinPurchase = signal<string>('0');
  randomprizeShopItem = signal<ShopOffersItem | null>(null);
  randomprizeCurrencySymbol = signal<string>('');

  maxReward = signal<number>(0);
  minReward = signal<number>(0);

  hasMinPurchase = computed(() => Number(this.randomprizeMinPurchase()) !== 0);

  percentExtra: number;
  randomprizeAveragePurchases: number = 0;

  ngOnInit(): void {
    const eventId = this.randomprizeModalData.data.eventId
      ? this.randomprizeModalData.data.eventId
      : this.randomprizeModalData.data.properties.eventId;

    const evt = this.eventsRepo.getEventById(eventId);
    this.randomprize.set(evt);
    this.lang.set(this.translateService.currentLang);

    this.discoverMinMaxReward();
    this.calculateMinPurchase();
  }

  closeModal(): void {
    this.eventManager.closeModal(new Modal(this.randomprizeModalData.id, ModalType.View, this.randomprize()));
  }

  openShop(): void {
    this.closeModal();

    this.eventManager.openModal(
      new Modal(NonPersistedModalName.ShopModalComponent, ModalType.View, {
        tab: 'left',
      })
    );
  }

  private calculateMinPurchase(): void {
    const evt = this.randomprize();
    if (!evt) return;

    const item = this.shopRatesRepo.getShopRates().Coins?.find((rate) => rate.sku === evt.properties.sku);

    this.randomprizeShopItem.set(item ?? null);
    this.randomprizeMinPurchase.set(item ? item.amount : '0');
    this.randomprizeCurrencySymbol.set(item ? item.currencySymbol : '');
  }

  private discoverMinMaxReward(): void {
    const evt = this.randomprize();
    if (!evt) return;

    from(evt.properties.ticketspermultiplier)
      .pipe(max((a, b) => (a.multiplier < b.multiplier ? -1 : 1)))
      .subscribe((item) => this.maxReward.set(item.multiplier));

    from(evt.properties.ticketspermultiplier)
      .pipe(max((a, b) => (a.multiplier > b.multiplier ? -1 : 1)))
      .subscribe((item) => this.minReward.set(item.multiplier));
  }
}
